'use client';

import React from 'react';
import { Icon } from '@benzinga/core-ui';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

export interface LearnCardItem {
  id?: number;
  slug?: string;
  name?: string;
  intro?: string;
  icon?: string;
  cover?: string;
  link?: string;
}

export interface LearnCardsProps {
  title?: string;
  subtitle?: string;
  cta_label?: string;
  learns?: LearnCardItem[];
}

const CARD =
  'group relative flex h-full min-h-[230px] flex-col overflow-hidden rounded-xl bg-white text-[#101010] transition-transform hover:-translate-y-1';

export const LearnCards: React.FC<LearnCardsProps> = ({ cta_label, learns, subtitle, title }) => {
  const cards = Array.isArray(learns) ? learns.filter(learn => learn?.name) : [];

  if (!title && !subtitle && cards.length === 0) return null;

  return (
    <section className="learn-cards w-full bg-[#061324] text-white">
      <div className="mx-auto max-w-[1400px] px-4 py-12 lg:px-8 lg:py-16">
        {(title || subtitle) && (
          <header className="mx-auto max-w-3xl text-center">
            {title && (
              <h1 className="m-0 text-[2.25rem] font-bold uppercase leading-[1.05] !text-white lg:text-[4.5rem]">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mx-auto mt-5 whitespace-pre-line text-base leading-relaxed text-bzblue-400 lg:mt-7 lg:text-lg">
                {subtitle}
              </p>
            )}
          </header>
        )}
        {cards.length > 0 && (
          <ul
            className={`m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-4 ${
              title || subtitle ? 'mt-10 border-t border-white/10 pt-10 lg:mt-14 lg:pt-14' : ''
            }`}
          >
            {cards.map((learn, index) => (
              <li className={CARD} key={learn.id ?? learn.slug ?? index}>
                {learn.cover && (
                  <>
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${learn.cover})` }}
                    />
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent"
                    />
                  </>
                )}
                <a className="relative flex flex-1 flex-col" href={learn.link || undefined}>
                  <div className="flex flex-1 flex-col p-5">
                    {learn.icon && (
                      <span className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(16,16,16,0.15)]">
                        <img alt="" className="h-8 w-8 object-contain" src={learn.icon} />
                      </span>
                    )}
                    <h3 className="m-0 text-[1.375rem] font-bold leading-tight">{learn.name}</h3>
                    {learn.intro && (
                      <p className="mb-0 mt-2.5 text-[0.9375rem] leading-relaxed text-bzgrey-500">{learn.intro}</p>
                    )}
                  </div>
                  {cta_label && (
                    <div className="px-5 pb-5">
                      <span className="inline-flex items-center gap-2 rounded-lg bg-[#061324] px-4 py-2.5 text-[0.9375rem] font-semibold text-white transition-colors duration-200 group-hover:bg-[#3EFFDC] group-hover:text-[#061324]">
                        {cta_label}
                        <Icon className="text-[0.6875rem]" icon={faChevronRight} />
                      </span>
                    </div>
                  )}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default LearnCards;
